import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Card, CardContent, CardHeader } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Icons } from "../../../utils/icons";
import EngagementModal from "../../modals/add-engagement"; // Import the EngagementModal component
import DeleteEngagementModal from "../../modals/delete-engagement-modal"; // Import the delete confirmation modal
import XanoAPI from "../../../lib/XanoAPI";

interface EngagementType {
  id: number;
  created_at: string;
  user: number;
  client: number;
  type: string;
  start_date: string;
  end_date: string;
  description: string;
}

interface EngagementsListProps {
  engagements: EngagementType[];
  clientId: number;
  onAddEngagement: (engagement: Partial<EngagementType>) => void;
}

const EngagementsList: React.FC<EngagementsListProps> = ({
  engagements,
  clientId,
  onAddEngagement,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [engagementToDelete, setEngagementToDelete] = useState<number | null>(
    null
  );

  const handleSaveEngagement = async (data: Partial<EngagementType>) => {
    try {
      const engagementData = {
        ...data,
        client: clientId,
        user: clientId, // Replace with actual user ID
      };

      const api = XanoAPI.getInstance();
      await api.addEngagement(engagementData);

      console.log("Engagement saved successfully:", engagementData);

      onAddEngagement(engagementData);

      setIsAddModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error("Failed to save engagement:", error);
    }
  };

  const handleDeleteEngagement = async () => {
    if (engagementToDelete) {
      try {
        const api = XanoAPI.getInstance();
        await api.deleteEngagement(engagementToDelete);
        console.log(`Engagement ${engagementToDelete} deleted successfully.`);
        window.location.reload();
      } catch (error) {
        console.error("Failed to delete engagement:", error);
      }
    }
    setEngagementToDelete(null);
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="my-6">
      <Card>
        <CardHeader>
          <h4 className="text-lg font-semibold text-primary">Engagements</h4>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>

                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {engagements.length > 0 ? (
                engagements.map((engagement) => (
                  <TableRow key={engagement.id}>
                    <TableCell>
                      {new Date(engagement.start_date).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {new Date(engagement.end_date).toLocaleString()}
                    </TableCell>
                    <TableCell>{engagement.description}</TableCell>
                    <TableCell>{engagement.type}</TableCell>

                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mr-2 hover:bg-red-500 hover:border-red-500"
                        onClick={() => {
                          setEngagementToDelete(engagement.id);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Icons.garbage className="w-4 h-4 " />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">
                    No engagements found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="mt-4">
            <Button
              className="bg-primary"
              onClick={() => setIsAddModalOpen(true)}
            >
              <Icons.calendar className="w-4 h-4 mr-2" />
              Add Engagement
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add Engagement Modal */}
      <EngagementModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveEngagement}
      />

      {/* Delete Confirmation Modal */}
      <DeleteEngagementModal
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteEngagement}
      />
    </div>
  );
};

export default EngagementsList;
